import { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import Spinner from "../../Components/Spinner";
import ErrorMessage from "../../Components/ErrorMessage";
import MandatorySign from "../../Components/MandatorySign";
import { MdStar, MdStart } from "react-icons/md";
import { TbPlus, TbStar } from "react-icons/tb";
import { LuTrash2 } from "react-icons/lu";
import SingleLoaderItem from "../../Loaders/SingleLoaderItem";
import ConfirmDeleteCardModal from "./ConfirmDeleteCardModal";
import { useLanguage } from "../../Translations/LenguageContext";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const CARD_WIDTH = 380;

const AllCardsModal = ({
    account_id,
    show,
    handleClose,
    cards,
    setShowNewCardModal,
    loadingPaymentData,
    fetchData
}) => {
    const {t} = useLanguage()
    const [loadingSave, setLoadingSave] = useState(false);
    const [errorMessage, setErrorMessage] = useState({ message: "", display: false });
    const [currentIndex, setCurrentIndex] = useState(0);
    const [defaultCardId, setDefaultCardId] = useState(null);
    const [loadingDelete, setLoadingDelete] = useState(false)
    const [selectedCardId, setSelectedCardId] = useState(null)
    const [showDeleteCardConfirm, setShowDeleteCardConfirm] = useState(false)
    const wrapperRef = useRef(null);

    const extendedCards = [
        ...cards,
        { id: "new_card", isNew: true }
    ];

    const scrollToIndex = (index) => {
        const el = wrapperRef.current;
        if (!el) return;

        const clampedIndex = Math.max(0, Math.min(index, extendedCards.length - 1));
        setCurrentIndex(clampedIndex);

        el.scrollTo({
            left: clampedIndex * CARD_WIDTH,
            behavior: "smooth",
        });
    };

    const scrollLeft = () => scrollToIndex(currentIndex - 1);
    const scrollRight = () => scrollToIndex(currentIndex + 1);

    const canScrollLeft = currentIndex > 0;
    const canScrollRight = currentIndex < extendedCards.length - 1;

    const handleSetDefault = (cardId) => {
        setDefaultCardId(cardId);
        setDefault(cardId)
    };

    const handleDeleteCard = (cardId) => {
        setShowDeleteCardConfirm(true)
        setSelectedCardId(cardId)
    };

    const setDefault = async (cardId) => {
        setLoadingSave(true);
        setErrorMessage({ message: "", display: false });

        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/set_default_payment`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id,
                    card_id: cardId
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Something went wrong");
            }

            fetchData()

        } catch (error) {
            setErrorMessage({
                message: error.message || "Server error",
                display: true
            });
        } finally {
            setLoadingSave(false);
        }
    };

    const deleteCard = async () => {
        setLoadingDelete(true);
        setErrorMessage({ message: "", display: false });

        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/delete_payment_method`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id,
                    card_id: selectedCardId
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Something went wrong");
            }

            fetchData()

        } catch (error) {
            setErrorMessage({
                message: error.message || "Server error",
                display: true
            });
        } finally {
            setShowDeleteCardConfirm(false)
            setLoadingDelete(false);
        }
    };

    const closeModal = () => {
        handleClose();
        setErrorMessage({ message: "", display: false });
        setLoadingDelete(false);
    };

    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;

        const handleScroll = () => {
            const index = Math.round(el.scrollLeft / CARD_WIDTH);
            setCurrentIndex(index);
        };

        el.addEventListener("scroll", handleScroll);

        return () => el.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        if (show && wrapperRef.current) {
            wrapperRef.current.scrollTo({ left: 0 });
            setCurrentIndex(0);
        }
    }, [show]);

    useEffect(() => {
        const defaultCard = cards.find(card => card.is_default);
        if (defaultCard) {
            setDefaultCardId(defaultCard.id);
        }
    }, [cards]);

    return (
        <Modal show={show} centered onHide={closeModal}>
            <div className="modal_header">
                <p className="modal_header_title">
                    {t.billing.your_card_wallet}{" "}
                    <MandatorySign
                        message={t.payments.sensitive_mex}
                        icon="info"
                        size="14px"
                        direction="right"
                    />
                </p>
            </div>

            <div className="modal_main_cont">
                {loadingPaymentData ? (
                    <div className="all_cards_cont">
                        <div className="cards_wrapper">
                            <div className="card_cont_btn">
                                <div className="card_cont_modal">
                                    <div className="card_loader">
                                        <div className="card_loader_header fade-in"></div>
                                        <div className="card_loader_center">
                                            <div className="card_loader_chip fade-in"></div>
                                            <div className="card_loader_date fade-in"></div>
                                        </div>
                                        <div className="card_loader_footer fade-in"></div>
                                    </div>
                                </div>
                                <div className="all_cards_cont_bottom">
                                    <SingleLoaderItem length={1} />
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="all_cards_cont">
                        <div className="cards_wrapper" ref={wrapperRef}>
                            {cards.length > 0 ? (
                                extendedCards.map((card, i) => {
                                    if (card.isNew) {
                                        return (
                                            <div className="new_card" key="new_card">
                                                <div className="new_card_inner">

                                                    <button className="add_card_btn" onClick={() => setShowNewCardModal(true)}>
                                                        + {t.billing.add_new_card}
                                                    </button>
                                                </div>
                                                <div className="all_cards_cont_bottom fade-in">
                                                    <div className="scroll_btn_cont">
                                                        {canScrollLeft && (
                                                            <button className="scroll_btn fade-in" onClick={scrollLeft}>
                                                                <i className="fa-solid fa-chevron-left"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }

                                    const isDefault = card.id === defaultCardId;

                                    return (
                                        <div className="card_cont_btn" key={card.id || i}>
                                            <div
                                                className={`card_cont_modal fade-in ${isDefault ? "default_card" : ""}`}
                                            >
                                                <div className="card_cont_inner">
                                                    <div className="card_cont_header">
                                                        <p>{card.payment_method_name || "Card"}</p>
                                                        {isDefault && (
                                                            <i><MdStar /></i>
                                                        )}
                                                        {!isDefault && (
                                                            <button className="card_cont_header_btn">
                                                                <i onClick={() => handleDeleteCard(card.id)}><LuTrash2 /></i>
                                                            </button>
                                                        )}
                                                    </div>

                                                    <div className="card_cont_chip_cont">
                                                        <div className="card_cont_chip"></div>
                                                        <p>
                                                            {String(card.exp_month).padStart(2, "0")}/
                                                            {String(card.exp_year).slice(-2)}
                                                        </p>
                                                    </div>

                                                    <div className="card_cont_footer">
                                                        <p>xxxx xxxx xxxx {card.last4}</p>
                                                        <p>{card.brand?.toUpperCase() || "CARD"}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="all_cards_cont_bottom fade-in">
                                                <div className="scroll_btn_cont">
                                                    {canScrollLeft && (
                                                        <button className="scroll_btn fade-in" onClick={scrollLeft}>
                                                            <i className="fa-solid fa-chevron-left"></i>
                                                        </button>
                                                    )}
                                                </div>

                                                {loadingSave ? (
                                                    <div className="is_default_cont fade-in">
                                                        <p><Spinner loading={true} /></p>
                                                    </div>
                                                ) : (
                                                    !isDefault ? (
                                                        <button
                                                            className="set_def_btn fade-in"
                                                            onClick={() => handleSetDefault(card.id)}
                                                        >
                                                            <MdStar /> {t.billing.set_default}
                                                        </button>
                                                    ) : (
                                                        <div className="is_default_cont fade-in">
                                                            <p className="fade-in"><TbStar /> {t.billing.default_card}</p>
                                                        </div>
                                                    )
                                                )}
                                                <div className="scroll_btn_cont">
                                                    {canScrollRight && (
                                                        <button className="scroll_btn fade-in" onClick={scrollRight}>
                                                            <i className="fa-solid fa-chevron-right"></i>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="no_card_item">
                                    <button
                                        className="header_wallet_btn"
                                        onClick={() => setShowNewCardModal(true)}
                                    >
                                        <i><TbPlus /></i> Add card
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className="modal_footer">
                <div className="modal_footer_err_cont">
                    <ErrorMessage err={errorMessage} />
                </div>

                <div className="modal_footer_btn_cont">
                    <button
                        className="modale_close_btn"
                        onClick={closeModal}
                        disabled={loadingSave}
                    >
                        Close
                    </button>
                </div>
            </div>

            <ConfirmDeleteCardModal
                account_id={account_id}
                show={showDeleteCardConfirm}
                handleClose={() => setShowDeleteCardConfirm(false)}
                deleteCard={deleteCard}
                fetchData={fetchData}
                loadingDelete={loadingDelete}
            />


        </Modal>
    );
};

export default AllCardsModal;